import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Stepper. Horizontal wizard progress. Mirrors .stepper /
 * .stepper__num / .stepper__bar in the showcase app.css: 30px pill circles
 * (done = green tint, active = primary fill, idle = muted), a 36×2px outline
 * connector bar between steps, and bold 12px labels (active emphasised).
 */
export interface StepperStep {
  label: string;
}

export interface StepperProps extends React.HTMLAttributes<HTMLOListElement> {
  steps: StepperStep[];
  current: number;
}

function stepState(index: number, current: number): 'done' | 'active' | 'idle' {
  if (index < current) return 'done';
  if (index === current) return 'active';
  return 'idle';
}

const numStateClasses: Record<'done' | 'active' | 'idle', string> = {
  done: 'bg-sober-green-bg text-sober-green-fg',
  active: 'bg-sober-primary-fg text-white',
  idle: 'bg-sober-muted text-sober-secondary',
};

export const Stepper = React.forwardRef<HTMLOListElement, StepperProps>(
  ({ className, steps, current, style, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn(
        'grid w-full items-start gap-0 sm:flex sm:w-auto sm:items-center sm:gap-2',
        className
      )}
      style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, ...style }}
      {...props}
    >
      {steps.map((step, index) => {
        const state = stepState(index, current);
        return (
          <li
            key={index}
            className="relative flex min-w-0 justify-center sm:items-center sm:justify-start sm:gap-2"
          >
            <div
              className="flex min-w-0 flex-col items-center gap-1 sm:flex-row sm:gap-2"
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'grid h-[30px] w-[30px] flex-none place-items-center rounded-full text-so-12 font-bold',
                  numStateClasses[state]
                )}
              >
                {index + 1}
              </span>
              <span
                className={cn(
                  'w-full min-w-0 break-words px-1 text-center text-so-12 font-bold leading-4 sm:w-auto sm:px-0 sm:text-left',
                  state === 'active' ? 'text-sober-text' : 'text-sober-secondary'
                )}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 ? (
              <span
                aria-hidden="true"
                className="absolute left-[calc(50%+17px)] top-[14px] h-0.5 w-[calc(100%-34px)] bg-sober-outline sm:static sm:h-0.5 sm:w-9 sm:flex-none"
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  )
);
Stepper.displayName = 'Stepper';
